import React, { Fragment, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReactComponent as ShoppingCart } from 'assets/icons/shopping-cart.svg';
import { ReactComponent as ChevronDown } from 'assets/icons/chevron-down.svg';
import { ReactComponent as ChevronUp } from 'assets/icons/chevron-up.svg';
import Cart from './Cart';

import { orderSummary as TEXT } from 'locales/en/cart';
import { useMediaQuery } from 'hooks';
import breakpoints from 'utils/breakpoints';
import { priceFromAPI, priceForAPI } from 'utils/price';

const OrderSummary = () => {
  const isLarge = useMediaQuery(breakpoints.lg);
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    subtotal,
    discountApplied,
    total,
    fees,
    userPaysFees,
    currencySymbol,
    charge,
    containsNonFundraiserProducts,
    stripeData,
    entities,
  } = useSelector((state) => state.cart);

  const {
    checkoutStep,
    stripeTerminalPreview,
    store,
  } = useSelector((state) => state.checkout);

  const onClickExpand = () => {
    setIsExpanded(!isExpanded);
  };


  const shippingAddressDisabled = useSelector(state => state.checkout.shippingAddressDisabled);

  let shippingFees = stripeData ? stripeData?.shipping_cost : 0;

  if (containsNonFundraiserProducts && stripeTerminalPreview && shippingAddressDisabled) {
    shippingFees = stripeTerminalPreview?.shipping?.amount || 0;
  }

  let vatTax = Number(stripeData?.tax) || 0;
  let taxRate = 0;
  let shippingCountry = null;
  let totalJavelinPrice = 0;


  if (containsNonFundraiserProducts && shippingAddressDisabled && stripeTerminalPreview) {

    shippingCountry = stripeTerminalPreview?.shipping?.address?.country;

    totalJavelinPrice = Object.values(entities).reduce((carry, current) => current.price * current.quantity, 0);

    taxRate = Number(
      stripeTerminalPreview.total_tax_amounts.reduce((carry, current) => carry + (current?.rate?.percentage || 0), 0)
    ).toFixed(2);

    const shippingTax = shippingFees * taxRate;
    const totalTerminalTax = Object.values(entities).reduce((carry, current) => carry + ((current?.price) * Number(taxRate)), 0)
    vatTax = +((shippingTax + totalTerminalTax) / 100);



    if (shippingCountry === 'US') {
      vatTax = +Object.values(entities).reduce((carry, current) => {

        const targetStripeHardware = stripeTerminalPreview.hardware_order_items.find(hardware => hardware.terminal_hardware_sku.id === current.productable.sku_id);
        const stripePrice = targetStripeHardware.amount * targetStripeHardware.quantity;
        const totalTaxAmounts = stripeTerminalPreview.total_tax_amounts || [];

        const javelinPrice = current.price;

        return totalTaxAmounts.reduce((taxCarry, taxCurrent) => {
          const percentage = (taxCurrent.rate.percentage / 100);
          const calculated = Number(percentage * (stripePrice + shippingFees)).toFixed(0);
          const includesShipping = calculated === Number(taxCurrent.amount).toFixed(0);

          let totalTax = (percentage) * (javelinPrice);

          if (includesShipping) {
            totalTax = (percentage) * (javelinPrice + shippingFees)
          }

          return Number(totalTax).toFixed(0);
        }, 0);
      }, 0) || 0;
    }
  }

  if (!shippingAddressDisabled) {
    vatTax = 0;
    shippingFees = 0;
  }

  const paymentCharge = containsNonFundraiserProducts ? shippingFees + priceForAPI(charge) + vatTax : priceForAPI(charge);

  return isLarge ? (
    <div className="bg-javelin-background min-h-screen pr-5 pl-10 xl:pr-14 2xl:pr-36 pt-20 w-auto col-span-6 xl:col-span-5">
      <Cart />
      <hr className="horizontal-line" />
      <div data-testid="summary" className="grid grid-cols-2 gap-y-4 py-8">
        <p className="text-sm text-javelin-gray-dark">{TEXT.subtotal}</p>
        <p
          data-testid="subtotal"
          className="text-sm text-javelin-gray-dark text-right font-bold"
        >
          {currencySymbol}
          {subtotal}
        </p>
        {(shippingAddressDisabled && Boolean(shippingFees)) && (
          <>
            <p className="text-sm text-javelin-gray-dark" >
              {TEXT.shipping}
            </p>
            <p className="text-sm text-javelin-gray-dark text-right font-bold" >
              {currencySymbol}
              {priceFromAPI(shippingFees)}
            </p>
          </>
        )}
        {(checkoutStep === 4 && !containsNonFundraiserProducts) && (
          <>
            <p className="text-sm text-javelin-gray-dark">
              {TEXT.discountApplied}
            </p>
            <p
              data-testid="discount"
              className="text-sm text-javelin-gray-dark text-right font-bold"
            >
              -{currencySymbol}
              {discountApplied}
            </p>
          </>
        )}
        {!containsNonFundraiserProducts && (
          <>
            <p className="text-sm text-javelin-gray-dark">{TEXT.fees}</p>
            <p
              data-testid="fees"
              className="text-sm text-javelin-gray-dark text-right font-bold"
            >
              {currencySymbol}
              {fees}
            </p>
          </>
        )}
        {containsNonFundraiserProducts && shippingAddressDisabled && (
          <>
            {
              // show each total_tax_amounts for Candian (CA) order
              shippingCountry === 'CA' && stripeTerminalPreview?.total_tax_amounts
                ? (
                  <>
                    {(stripeTerminalPreview?.total_tax_amounts || []).map((item) =>
                    (
                      <Fragment key={item.rate.display_name}>
                        <p className="text-sm text-javelin-gray-dark">
                          {item?.rate?.display_name}{item?.rate?.percentage && ' (' + item.rate.percentage + '%)'}
                        </p>
                        <p
                          data-testid="fees"
                          className="text-sm text-javelin-gray-dark text-right font-bold"
                        >
                          {currencySymbol}
                          {priceFromAPI(Number((item.rate.percentage / 100) * (shippingFees + totalJavelinPrice)))}
                        </p>
                      </Fragment>
                    )
                    )}
                  </>
                )
                : (
                  <>
                    <p className="text-sm text-javelin-gray-dark">
                      {TEXT.tax}{taxRate && ' (' + taxRate + '%)'}
                    </p>
                    <p
                      data-testid="fees"
                      className="text-sm text-javelin-gray-dark text-right font-bold"
                    >
                      {currencySymbol}
                      {priceFromAPI(vatTax)}
                    </p>
                  </>
                )
            }
          </>
        )}
      </div>
      <hr className="horizontal-line block" />
      <div data-testid="total" className="grid grid-cols-2 py-8">
        <p className="text-sm text-javelin-gray-dark">{TEXT.total}</p>
        <p
          data-testid="total"
          className="text-xl text-javelin-gray-dark text-right font-bold"
        >
          {currencySymbol}
          {priceFromAPI(paymentCharge)}
        </p>
      </div>
      {
        containsNonFundraiserProducts && (
          <>
            <hr className='horizontal-line-block' />
            <p className='text-javelin-gray-dark text-opacity-30 mt-8' >
              {store?.promotional_text}
            </p>
          </>
        )
      }
    </div >
  ) : (
    <div className="bg-white min-h-0 px-3 pt-16 w-screen">
      <div className={`${isExpanded ? 'h-auto' : 'h-14'}`}>
        <div className="flex items-center justify-between h-14">
          <div
            className="flex justify-between items-center"
            onClick={onClickExpand}
          >
            <ShoppingCart className="mr-2 w-5 h-5 text-javelin-gray-dark fill-current" />
            <p className="font-bold text-sm text-javelin-gray-dark w-40">
              {isExpanded ? TEXT.hide : TEXT.show}
            </p>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 my-auto text-javelin-blue-dark fill-current cursor-pointer" />
            ) : (
              <ChevronDown className="w-4 h-4 my-auto text-javelin-blue-dark fill-current cursor-pointer" />
            )}
          </div>
          <p className="font-bold text-sm text-javelin-gray-dark text-right">
            {currencySymbol}
            {total}
          </p>
        </div>
        {isExpanded && (
          <>
            <Cart />
            <div data-testid="summary" className="grid grid-cols-2 gap-y-1">
              <p className="text-sm text-javelin-gray-dark text-opacity-30">
                {TEXT.subtotal}
              </p>
              <p className="text-sm text-javelin-gray-dark text-right">
                {currencySymbol}
                {subtotal}
              </p>
              {(shippingAddressDisabled && Boolean(shippingFees)) && (
                <>
                  <p className="text-sm text-javelin-gray-dark text-opacity-30" >
                    {TEXT.shipping}
                  </p>
                  <p className="text-sm text-javelin-gray-dark text-right" >
                    {currencySymbol}
                    {priceFromAPI(shippingFees)}
                  </p>
                </>
              )}
              {checkoutStep === 4 && (
                <>
                  <p className="text-sm text-javelin-gray-dark text-opacity-30">
                    {TEXT.discountApplied}
                  </p>
                  <p className="text-sm text-javelin-gray-dark text-right">
                    -{currencySymbol}
                    {discountApplied}
                  </p>
                </>
              )}
              <p className="text-sm text-javelin-gray-dark text-opacity-30">
                {TEXT.fees}
              </p>
              <p className="text-sm text-javelin-gray-dark text-right">
                {currencySymbol}
                {userPaysFees ? fees : '0.00'}
              </p>
              {containsNonFundraiserProducts && shippingAddressDisabled && (
                <>
                  {
                    // show each total_tax_amounts for Candian (CA) order
                    shippingCountry === 'CA' && stripeTerminalPreview?.total_tax_amounts
                      ? (
                        <>
                          {(stripeTerminalPreview?.total_tax_amounts || []).map((item) =>
                          (
                            <Fragment key={item.rate.display_name}>
                              <p className="text-sm text-javelin-gray-dark text-opacity-30">
                                {item?.rate?.display_name}{item?.rate?.percentage && ' (' + item.rate.percentage + '%)'}
                              </p>
                              <p className="text-sm text-javelin-gray-dark text-right">
                                {currencySymbol}
                                {priceFromAPI(Number((item.rate.percentage / 100) * (shippingFees + totalJavelinPrice)))}
                              </p>
                            </Fragment>
                          )
                          )}
                        </>
                      )
                      : (
                        <>
                          <p className="text-sm text-javelin-gray-dark text-opacity-30">
                            {TEXT.tax}{taxRate && ' (' + taxRate + '%)'}
                          </p>
                          <p className="text-sm text-javelin-gray-dark text-right">
                            {currencySymbol}
                            {priceFromAPI(vatTax)}
                          </p>
                        </>
                      )
                  }

                </>
              )}
            </div>
            <div data-testid="total" className="grid grid-cols-2 py-3">
              <p className="text-sm text-javelin-gray-dark font-bold text-opacity-30">
                {TEXT.total}
              </p>
              <p className="text-sm text-javelin-gray-dark text-right font-bold">
                {currencySymbol}
                {priceFromAPI(paymentCharge)}
              </p>
            </div>
            {containsNonFundraiserProducts && (
              <>
                <hr className='horizontal-line-block' />
                <p className='text-javelin-gray-dark text-opacity-30 my-4' >
                  {store?.promotional_text}
                </p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OrderSummary;
